import type { AgentId, Sop } from './agents-types';

export const SOPS: Sop[] = [
  {
    id: 'PRC-SOP-001',
    agentId: 'director',
    title: 'Coordinating the procurement agents',
    version: 2,
    ownerId: 'marcus-hale',
    lastReviewed: '2026-09-14',
    steps: [
      {
        title: 'Read what came in',
        instruction:
          'Take every new alert, request, handoff and question from the other agents, from Teams and from the team channel. Note who raised it, what it touches and how urgent it is.',
        uses: ['Team channel', 'Teams', 'Risk alerts', 'Requests'],
      },
      {
        title: 'Assign it to one agent',
        instruction:
          'Pick the agent whose procedure covers the task and say what is needed in one or two sentences. Never do another agent’s work.',
        uses: ['Agent procedures', 'Agent roster'],
      },
      {
        title: 'Bring in the right agents in order',
        instruction:
          'When a task needs several agents, ask them one at a time and pass each answer on, for example exposure first, then the backup supplier, then the price check.',
        uses: ['Team channel'],
      },
      {
        title: 'Settle disagreements',
        instruction:
          'When two agents want different actions, follow the stricter policy, post the reason in the channel and tell both agents what was decided.',
        uses: ['Spend policy', 'Agent guardrails'],
      },
      {
        title: 'Escalate decisions to a person',
        instruction:
          'Send any decision that commits money above the limit, changes a supplier or changes a contract to a named approver, with the numbers and the reason attached. Chase it if nobody answers.',
        uses: ['Approval matrix', 'Teams'],
      },
      {
        title: 'Answer questions',
        instruction:
          'Answer questions from the team in plain language with the figures and links behind them. Ask the agent who owns the topic when the director does not have the facts. Only use data the person is allowed to see.',
        uses: ['Audit log', 'Agent activity', 'Permissions'],
      },
      {
        title: 'Close the loop',
        instruction:
          'Post a short summary of open items and who owns each one. Record every assignment, handoff and escalation in the audit log.',
        uses: ['Team channel', 'Audit log'],
      },
    ],
  },
  {
    id: 'PRC-SOP-003',
    agentId: 'intake',
    title: 'Intake classification and routing',
    version: 4,
    ownerId: 'daniel-reyes',
    lastReviewed: '2026-07-21',
    steps: [
      {
        title: 'Capture the request',
        instruction:
          'Read the request as written in Teams, email or the form. Extract what is needed, the amount, the requester, the need-by date and the cost center (the budget it is charged to).',
        uses: ['Teams', 'Email', 'Request form'],
      },
      {
        title: 'Classify the request',
        instruction:
          'Decide direct or indirect, pick the category and the receiving site. Ask the requester one clarifying question when the category is ambiguous.',
        uses: ['Category list', 'Cost center map'],
      },
      {
        title: 'Find what already exists',
        instruction:
          'Look for an active agreement, a catalog item or a preferred supplier before anything goes to market.',
        uses: ['Contracts repository', 'Catalogs'],
      },
      {
        title: 'Run the duplicate check',
        instruction:
          'Compare against open requests and POs from the last 90 days for the same item, supplier and site. Stop a duplicate and link the original.',
        uses: ['Request history', 'PO history'],
      },
      {
        title: 'Apply spend policy',
        instruction:
          'Check budget, the approval matrix and the bid threshold. Above the threshold, three competitive bids are required.',
        uses: ['Spend policy', 'Approval matrix', 'Budget'],
      },
      {
        title: 'Prepare the compliance review',
        instruction:
          'Check supplier status, contract terms, agreed hourly rates and caps so the review is complete before the approver opens the request.',
        uses: ['Supplier records', 'Clause index'],
      },
      {
        title: 'Route and create the order',
        instruction:
          'Route approvals to the right people in Teams. On-contract requests inside the limit become a PO; the rest go to Legal or Sourcing.',
        uses: ['Teams', 'ERP purchase orders'],
      },
    ],
  },
  {
    id: 'SQA-SOP-021',
    agentId: 'onboarding',
    title: 'Supplier qualification and onboarding',
    version: 5,
    ownerId: 'priya-raman',
    lastReviewed: '2026-08-30',
    steps: [
      {
        title: 'Open the case and send the checklist',
        instruction:
          'Create the onboarding case from the approved request. Send the supplier the document checklist for its category.',
        uses: ['Onboarding cases', 'Email'],
      },
      {
        title: 'Verify company profile, tax and bank',
        instruction:
          'Match legal name, registration and tax ID. Verify that the bank account belongs to the supplier with a bank letter and a callback to a number from an independent source.',
        uses: ['Company registry', 'Tax ID lookup', 'Phone callback'],
      },
      {
        title: 'Score credit and financial health',
        instruction:
          'Pull the credit score and the last three years of financials where available. Flag a score below the category threshold.',
        uses: ['Credit bureau feed', 'Financial statements'],
      },
      {
        title: 'Screen sanctions, negative news and litigation',
        instruction:
          'Screen the company, owners and sites against sanctions lists, negative news and litigation records. Hold the case on any possible match.',
        uses: ['Sanctions lists', 'News feed', 'Court records'],
      },
      {
        title: 'Check quality credentials',
        instruction:
          'Verify UL listings and file numbers, CE marking (the European safety mark), ISO 9001 certificates and insurance. Check validity dates against the first expected PO.',
        uses: [
          'Certificate registry',
          'UL product directory',
          'Insurance certificates',
        ],
      },
      {
        title:
          'Collect the confidentiality and quality agreements and the ESG questionnaire',
        instruction:
          'Track signature of the confidentiality agreement and quality agreement and the questionnaire on environmental, social and governance practices (ESG). Chase missing items, then escalate to the buyer.',
        uses: ['Contracts repository', 'ESG questionnaire'],
      },
      {
        title: 'Prepare the approval packet',
        instruction:
          'Summarize findings and open items for the approvers. The agent never approves a supplier.',
        uses: ['Approval workflow', 'Teams'],
      },
    ],
  },
  {
    id: 'LEG-SOP-009',
    agentId: 'contracts',
    title: 'Contract summaries and obligation tracking',
    version: 2,
    ownerId: 'elena-novak',
    lastReviewed: '2026-06-18',
    steps: [
      {
        title: 'Ingest the agreement and its exhibits',
        instruction:
          'Read the signed PDF with every exhibit, statement of work and amendment. Link children to the parent agreement.',
        uses: ['Contracts repository', 'Document text reader'],
      },
      {
        title: 'Extract clauses with section references',
        instruction:
          'Extract payment terms, pricing and price increases, delivery terms, liability, termination, service levels, data terms and governing law. Keep the section number and a confidence score.',
        uses: ['Clause index', 'Contract documents'],
      },
      {
        title: 'Flag low-confidence and non-standard clauses',
        instruction:
          'Send clauses below the confidence threshold or outside the standard positions to Legal for review.',
        uses: ['Clause playbook', 'Legal review list'],
      },
      {
        title: 'Answer questions with citations only',
        instruction:
          'Answer from clause text and cite the section. Decline when no clause supports the answer and offer to ask Legal.',
        uses: ['Clause index', 'Ask panel'],
      },
      {
        title: 'Track renewals, notice deadlines and obligations',
        instruction:
          'Compute notice deadlines from end dates and notice periods. Track obligations such as change-notification periods and audit rights.',
        uses: ['Renewal calendar', 'Obligation tracker'],
      },
      {
        title: 'Notify the contract owner',
        instruction:
          'Alert the owner in Teams with the clause, the date and a suggested action.',
        uses: ['Teams', 'Email'],
      },
    ],
  },
  {
    id: 'AP-SOP-014',
    agentId: 'compliance',
    title: 'Contract compliance on supplier invoices',
    version: 3,
    ownerId: 'tom-gallagher',
    lastReviewed: '2026-08-12',
    steps: [
      {
        title: 'Identify the governing agreement(s)',
        instruction:
          'Match the supplier and PO to the active agreement, plus any statement of work, exhibit or amendment that governs the invoice.',
        uses: ['Contracts repository', 'PO and receipt lookup'],
      },
      {
        title: 'Read the extracted clauses',
        instruction:
          'Pull the clauses that apply: payment terms, price and price increase caps, delivery terms, agreed hourly rates, budget caps, surcharge caps, uptime credits and rebates.',
        uses: ['Clause index', 'Contract documents'],
      },
      {
        title: 'Check each invoice line',
        instruction:
          'Compare the header terms and every line with the clause rule. Record the clause section behind each finding.',
        uses: ['Invoice capture', 'Clause rules'],
      },
      {
        title: 'Quantify the variance',
        instruction:
          'Calculate the overbilling, the recoverable amount and the payment-timing effect. Confirm quantities against goods receipts.',
        uses: ['PO, receipt and invoice match', 'Calculator'],
      },
      {
        title: 'Decide the action under policy',
        instruction:
          'Hold, pay less than billed, pay on contract terms or escalate, based on the guardrails and the size of the dispute.',
        uses: ['Guardrails', 'AP payment policy'],
      },
      {
        title: 'Draft and send the dispute',
        instruction:
          'Write the dispute with clause citations from the ap-disputes mailbox. Pay less than billed and schedule payment on contract terms when the limit allows it.',
        uses: ['Email (ap-disputes@)', 'Payment run scheduler'],
      },
      {
        title: 'Log the outcome',
        instruction:
          'Record the reasoning, the approver and the recovery in the audit log. Update the supplier scorecard.',
        uses: ['Audit log', 'Supplier scorecard'],
      },
    ],
  },
  {
    id: 'PRC-SOP-011',
    agentId: 'sourcing',
    title: 'Competitive sourcing and bid comparison',
    version: 3,
    ownerId: 'sarah-chen',
    lastReviewed: '2026-09-04',
    steps: [
      {
        title: 'Draft the request for bids from the category template',
        instruction:
          'Fill the template from the request, the spec and the volume forecast. Set the weighted criteria for the category.',
        uses: ['Request templates', 'Material specs', 'Forecast'],
      },
      {
        title: 'Qualify and invite bidders',
        instruction:
          'Invite incumbents and qualified alternates. Check supplier status and risk before the invitation goes out.',
        uses: ['Supplier records', 'Risk scores'],
      },
      {
        title: 'Collect and standardize bids',
        instruction:
          'Read bids from email and portal, convert units and currencies to one basis, and chase late bidders.',
        uses: ['Email', 'Supplier portal', 'Exchange rates'],
      },
      {
        title: 'Compare bids on delivered cost',
        instruction:
          'Add tariff, duty and freight to each unit price so bids compare on the same basis.',
        uses: ['Tariff tables', 'Freight rates'],
      },
      {
        title: 'Score against the weighted criteria',
        instruction:
          'Score delivered cost, quality and certification, supply security, lead time, sustainability and commercial terms with the weights set for that bid.',
        uses: ['Scoring model', 'Quality records'],
      },
      {
        title: 'Check risk and compliance flags',
        instruction:
          'Flag sanctions, forced-labor import, financial and single-source concerns on each bidder before recommending an award.',
        uses: ['Risk alerts', 'Sanctions lists'],
      },
      {
        title: 'Recommend the award',
        instruction:
          'Propose an award split with the savings against current spend. The buyer decides and publishes.',
        uses: ['Award model', 'Approval workflow'],
      },
    ],
  },
  {
    id: 'PRC-SOP-015',
    agentId: 'negotiation',
    title: 'Supplier negotiation playbook',
    version: 2,
    ownerId: 'sarah-chen',
    lastReviewed: '2026-09-12',
    steps: [
      {
        title: 'Build the brief',
        instruction:
          'Collect spend, performance, total cost of ownership, contract terms and leverage points for the supplier.',
        uses: ['Supplier profile', 'Total cost model', 'Contracts repository'],
      },
      {
        title: 'Set target, walk-away and tradeable terms',
        instruction:
          'Take the target and walk-away from the playbook. List what can be traded: volume share, payment terms, warranty, term length.',
        uses: ['Negotiation playbook'],
      },
      {
        title: 'Open with the first counter',
        instruction:
          'Send the counter by email or place the call. State that you are an AI assistant acting for Pillar Industrial Supply.',
        uses: ['Email', 'Phone'],
      },
      {
        title: 'Counter inside the concession limit',
        instruction:
          'Trade price against terms. Do not concede more than the limit below target or agree to terms outside the playbook.',
        uses: ['Guardrails', 'Negotiation playbook'],
      },
      {
        title: 'Escalate when needed',
        instruction:
          'Hand over to the category manager when the supplier rejects the second counter or asks for something outside the playbook.',
        uses: ['Teams'],
      },
      {
        title: 'Summarize for approval',
        instruction:
          'Write up the agreed terms, the value and the open points. A buyer approves before anything is signed or sent.',
        uses: ['Approval workflow', 'Audit log'],
      },
    ],
  },
  {
    id: 'SCM-SOP-007',
    agentId: 'followup',
    title: 'PO follow-up and rush orders',
    version: 6,
    ownerId: 'marcus-hale',
    lastReviewed: '2026-08-02',
    steps: [
      {
        title: 'Confirm acknowledgment',
        instruction:
          'Check for an electronic order confirmation or an email acknowledgment. Chase the supplier when none arrives inside the window.',
        uses: ['Electronic order confirmations', 'Email'],
      },
      {
        title: 'Read the delivery date',
        instruction:
          'Read promise dates from supplier emails, portals and electronic ship notices. Keep the source with each date.',
        uses: ['Email', 'Supplier portal', 'Electronic ship notices'],
      },
      {
        title: 'Compare with the need date and days of stock',
        instruction:
          'Work out how many days of stock the receiving site has against the new promise date.',
        uses: ['Inventory', 'Replenishment engine'],
      },
      {
        title: 'Rush or escalate',
        instruction:
          'Ask for a rush when days of stock are inside the limit. Escalate to the buyer when the delivery date slips beyond the threshold.',
        uses: ['Email', 'Phone', 'Teams'],
      },
      {
        title: 'Update promise dates',
        instruction:
          'Write the confirmed date to the PO line. Never change price or quantity.',
        uses: ['ERP purchase orders'],
      },
      {
        title: 'Log the outcome',
        instruction:
          'Record what was read, what was asked and what the supplier committed to.',
        uses: ['Audit log'],
      },
    ],
  },
  {
    id: 'SQA-SOP-030',
    agentId: 'risk',
    title: 'Continuous supplier risk monitoring',
    version: 3,
    ownerId: 'marcus-hale',
    lastReviewed: '2026-09-18',
    steps: [
      {
        title: 'Ingest signals',
        instruction:
          'Read quality records, credit scores, sanctions and negative-news feeds, cyber incident notices, regulatory changes and weather.',
        uses: ['Quality system', 'Credit feed', 'Sanctions feed', 'News'],
      },
      {
        title: 'Match each signal to the business',
        instruction:
          'Link the signal to the supplier, site, material, contract and open competitive bids it touches.',
        uses: ['Supplier records', 'Contracts repository', 'Material records'],
      },
      {
        title: 'Score the exposure',
        instruction:
          'Combine likelihood, spend, single-source status and weeks of supply into a severity.',
        uses: ['Risk model', 'Inventory'],
      },
      {
        title: 'Recommend an action',
        instruction:
          'Propose the next step with the owner, for example a corrective action request, a second supplier or a request for a SOC 2 update letter.',
        uses: ['Risk playbook'],
      },
      {
        title: 'Notify the owner',
        instruction:
          'Send critical alerts to the category owner in Teams at once. Batch lower severities into the daily digest.',
        uses: ['Teams', 'Email'],
      },
      {
        title: 'Track to closure',
        instruction:
          'Follow open alerts until the action is done and re-score the supplier.',
        uses: ['Alert tracker'],
      },
    ],
  },
  {
    id: 'SCM-SOP-012',
    agentId: 'portal',
    title: 'Supplier portal procedures',
    version: 2,
    ownerId: 'kevin-oneill',
    lastReviewed: '2026-07-09',
    steps: [
      {
        title: 'Select the recorded procedure',
        instruction:
          'Pick the approved recording for this supplier portal and task, for example check order status or download invoices.',
        uses: ['Procedure library'],
      },
      {
        title: 'Retrieve credentials from the secure store',
        instruction:
          'Request a short-lived session from the secure credential store. Passwords are never shown or logged.',
        uses: ['Secure credential store'],
      },
      {
        title: 'Replay the steps',
        instruction:
          'Replay each step and verify the screen against the recording before the next click.',
        uses: ['Browser automation'],
      },
      {
        title: 'Stop on an unseen screen',
        instruction:
          'When a screen differs from the recording, stop, capture it and ask a person to continue or re-record.',
        uses: ['Teams', 'Screen capture'],
      },
      {
        title: 'File the output',
        instruction:
          'Store downloads and status results where the next workflow expects them: AP intake, the quality system or the PO.',
        uses: ['Document store', 'ERP purchase orders'],
      },
      {
        title: 'Log the session',
        instruction:
          'Record the procedure, duration, pages visited and result in the audit log.',
        uses: ['Audit log'],
      },
    ],
  },
  {
    id: 'SCM-SOP-004',
    agentId: 'demand',
    title: 'Demand review and bill of materials forecast',
    version: 2,
    ownerId: 'marcus-hale',
    lastReviewed: '2026-09-09',
    steps: [
      {
        title: 'Read the statistical forecast',
        instruction:
          'Take the forecast for each item at each site as the forecast engine produced it, with its method, demand pattern and accuracy on closed periods. Never change an approved forecast.',
        uses: ['Forecast engine', 'Demand history'],
      },
      {
        title: 'Explain what moved it',
        instruction:
          'State in plain language what changed since the last cycle: trend, seasonality, recent actual demand, overrides and unusual months.',
        uses: ['Forecast details', 'Seasonality profiles'],
      },
      {
        title: 'Flag unusual months',
        instruction:
          'Apply the unusual-month rules to each month of history. Flag one-time project orders, promotions and data errors, and propose excluding them from the baseline.',
        uses: ['Unusual-month rules', 'Sales orders'],
      },
      {
        title: 'Propose overrides',
        instruction:
          'When a signed program, contractor release or price action is not in the history, propose an override with a reason and a period. A planner approves it.',
        uses: ['Sales notes', 'Override log', 'Approval workflow'],
      },
      {
        title: 'Break panels down into components',
        instruction:
          'Multiply the control panel shop forecast for finished panels by each bill of materials to get component demand by month.',
        uses: ['Bills of materials', 'Finished-panel forecast'],
      },
      {
        title: 'Subtract stock and time the orders',
        instruction:
          'Subtract stock on hand, open POs and reserve stock from component demand, then place the shortfall earlier by the supplier lead time as orders to place.',
        uses: ['Inventory', 'Open POs', 'Vendor lead times'],
      },
      {
        title: 'Check holds and propose backup suppliers',
        instruction:
          'Flag components that are on hold, quarantined or under a risk alert. Suggest a qualified backup supplier with its cost and a time window.',
        uses: ['Risk alerts', 'Approved supplier list'],
      },
      {
        title: 'Hand off to replenishment',
        instruction:
          'Send orders to place to the replenishment page as drafts. Record every explanation, override, exclusion and approval in the audit log.',
        uses: ['Replenishment', 'Audit log'],
      },
    ],
  },
  {
    id: 'SCM-SOP-001',
    agentId: 'planner',
    title: 'Replenishment suggestion review',
    version: 4,
    ownerId: 'marcus-hale',
    lastReviewed: '2026-08-25',
    steps: [
      {
        title: 'Receive the engine suggestion',
        instruction:
          'Take the suggested supplier, quantity and date from the four-phase replenishment engine without changing them. The engine plans from the approved forecast.',
        uses: ['Replenishment engine'],
      },
      {
        title: 'Explain the drivers',
        instruction:
          'State why in plain language: demand, policy, lead time, days of stock and constraints.',
        uses: ['Demand history', 'Inventory policy'],
      },
      {
        title: 'Check vendor constraints',
        instruction:
          'Compare the cart with the supplier’s minimum order quantity, minimum order value and freight rules and say what would close the gap.',
        uses: ['Supplier constraints', 'Freight rules'],
      },
      {
        title: 'Look for buying ahead and volume prices',
        instruction:
          'Suggest a larger quantity when a volume price or buying ahead saves more than the cost of holding the stock.',
        uses: ['Volume prices', 'Cost of holding stock'],
      },
      {
        title: 'Present to the buyer',
        instruction:
          'Show the suggestion and the explanation on the replenishment page. The buyer confirms or overrides.',
        uses: ['Replenishment'],
      },
      {
        title: 'Record overrides',
        instruction:
          'Keep the buyer reason for any override so the engine policy can be reviewed.',
        uses: ['Audit log'],
      },
    ],
  },
];

export function getSop(agentId: AgentId): Sop {
  const found = SOPS.find((sop) => sop.agentId === agentId);
  if (!found) {
    throw new Error(`No SOP for agent: ${agentId}`);
  }
  return found;
}
